import { useState, FC, Dispatch, SetStateAction, useMemo } from 'react';
import { Modal } from '@wix/design-system';
import { useExperiments } from '@wix/fe-essentials-standalone';
import { SHOULD_SUPPORT_HINDI_SPEC } from '@utils/constants';
import {
  FEATURES,
  Filters,
  INDUSTRIES,
  LANGUAGES_WITHOUT_ALL,
  PRICES,
  SITE_TYPES,
  STYLES,
} from '@common/types';
import TemplatesMobileFiltersMenu from './TemplatesMobileFiltersMenu';
import SingleSelectionFilterScreen from './SingleSelectionFilterScreen';
import MultiSelectionFilterScreen from './MultiSelectionFilterScreen';
import { useTemplatesBi } from '@components/Hooks/useTemplatesBi';
import { updateQueryParams } from '@utils/route';
import { useShallowRouter } from '@components/Hooks/useRouter';

export type FilterModalScreens = keyof Filters | 'home';

type IClearAllOptions = keyof Filters | 'all';
export type IClearAllRecord = Partial<Record<IClearAllOptions, boolean>>;
export type IOnClear = (clearAllOption: IClearAllOptions) => void;

const MobileFilterModal: FC<{
  isOpen: boolean;
  setIsFilterMenuOpen: Dispatch<SetStateAction<boolean>>;
}> = ({ isOpen, setIsFilterMenuOpen }) => {
  const [clearAllRecord, setClearAllRecord] = useState<IClearAllRecord>({});
  const [currentScreen, setCurrentScreen] =
    useState<FilterModalScreens>('home');
  const { logBiClick } = useTemplatesBi({ is_mobile: true });
  const { router, navigate } = useShallowRouter();
  const { experiments } = useExperiments({ readOnly: true });
  const shouldSupportHindi = experiments.enabled(SHOULD_SUPPORT_HINDI_SPEC);

  const languages = useMemo(() => {
    const entries = Object.entries(LANGUAGES_WITHOUT_ALL).filter(
      ([key]) => key !== 'HINDI' || shouldSupportHindi,
    );
    return Object.freeze(Object.fromEntries(entries));
  }, [shouldSupportHindi]);

  const onClose = () => {
    logBiClick({ click_type: 'Exit filters' });
    setIsFilterMenuOpen(false);
  };

  const onClear = (clearAllScreen: IClearAllOptions) => {
    logBiClick({ click_type: `${currentScreen} filter - Clear all filters` });
    setClearAllRecord((current) => ({ ...current, [clearAllScreen]: true }));
  };

  const onClearAll = () => {
    logBiClick({ click_type: 'Clear all filters' });
    navigate(updateQueryParams({}, router, { all: true }));
    setIsFilterMenuOpen(false);
  };

  const onBack = () => {
    logBiClick({ click_type: `${currentScreen} filter - close` });
    setCurrentScreen('home');
  };

  const renderCurrentScreen = (selection: FilterModalScreens) => {
    switch (selection) {
      case 'home':
        return (
          <TemplatesMobileFiltersMenu
            setCurrentScreen={setCurrentScreen}
            clearAllRecord={clearAllRecord}
            onClose={onClose}
            onClear={onClearAll}
          />
        );
      case 'price':
        return (
          <SingleSelectionFilterScreen
            onClose={onClose}
            onBack={onBack}
            items={PRICES}
            filterType={selection}
            onClear={onClear}
            clearAllRecord={clearAllRecord}
          />
        );
      case 'features':
        return (
          <MultiSelectionFilterScreen
            onClose={onClose}
            onBack={onBack}
            items={FEATURES}
            filterType={selection}
            onClear={onClear}
            clearAllRecord={clearAllRecord}
          />
        );
      case 'industries':
        return (
          <MultiSelectionFilterScreen
            onClose={onClose}
            onBack={onBack}
            items={INDUSTRIES}
            filterType={selection}
            onClear={onClear}
            clearAllRecord={clearAllRecord}
          />
        );
      case 'languages':
        return (
          <MultiSelectionFilterScreen
            onClose={onClose}
            onBack={onBack}
            items={languages}
            filterType={selection}
            onClear={onClear}
            clearAllRecord={clearAllRecord}
          />
        );
      case 'siteType':
        return (
          <SingleSelectionFilterScreen
            onClose={onClose}
            onBack={onBack}
            items={SITE_TYPES}
            filterType={selection}
            onClear={onClear}
            clearAllRecord={clearAllRecord}
          />
        );
      case 'styles':
        return (
          <MultiSelectionFilterScreen
            onClose={onClose}
            onBack={onBack}
            items={STYLES}
            filterType={selection}
            onClear={onClear}
            clearAllRecord={clearAllRecord}
          />
        );
      default:
        return (
          <TemplatesMobileFiltersMenu
            setCurrentScreen={setCurrentScreen}
            clearAllRecord={clearAllRecord}
            onClose={onClose}
            onClear={onClearAll}
          />
        );
    }
  };

  return <Modal isOpen={isOpen}>{renderCurrentScreen(currentScreen)}</Modal>;
};

export default MobileFilterModal;
